import { useSearchParams } from "react-router-dom";
import { UserRequestSort, UserRequestStatus } from "../types/user-request";

export type StatusFilterValue = UserRequestStatus | "ALL";

// URL-driven filter state for the Pending Requests lists — mirrors how
// users/sessions pages keep filters in the query string. `search` is owned by
// the shared <SearchBar> (param: `search`); this manages the rest. Any filter
// change resets `page` to 0; `tab` is preserved across resets.
export function useUserRequestFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const status = (searchParams.get("status") as StatusFilterValue) || "OPEN";
  const sort = (searchParams.get("sort") as UserRequestSort) || "newest";
  const dateFrom = searchParams.get("date_from") || undefined;
  const dateTo = searchParams.get("date_to") || undefined;

  const apply = (mutate: (p: URLSearchParams) => void, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    if (resetPage) next.set("page", "0");
    setSearchParams(next);
  };

  const setStatus = (value: StatusFilterValue) =>
    apply((p) => p.set("status", value));

  const setSort = (value: UserRequestSort) => apply((p) => p.set("sort", value));

  const setDateRange = (from: Date | null, to: Date | null) =>
    apply((p) => {
      if (from) p.set("date_from", from.toISOString());
      else p.delete("date_from");
      if (to) {
        const eod = new Date(to);
        eod.setHours(23, 59, 59, 999);
        p.set("date_to", eod.toISOString());
      } else {
        p.delete("date_to");
      }
    });

  const setPage = (value: number) => apply((p) => p.set("page", String(value)), false);

  // Clear filters but keep which tab is open.
  const reset = () => {
    const next = new URLSearchParams();
    const tab = searchParams.get("tab");
    if (tab) next.set("tab", tab);
    setSearchParams(next);
  };

  const filtersActive =
    status !== "OPEN" ||
    !!dateFrom ||
    !!dateTo ||
    search.trim() !== "" ||
    sort !== "newest";

  return {
    page,
    search,
    status,
    sort,
    dateFrom,
    dateTo,
    filtersActive,
    setStatus,
    setSort,
    setDateRange,
    setPage,
    reset,
  };
}
